// Imports
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { t } from "i18next";
import { MdFilePresent, MdPictureAsPdf } from "react-icons/md";
import { expiredDate, formatDate } from "util/datelib";
import CircleButton from "widgets/controls/buttons/circle-button";
import Badge from "widgets/shared/badge";

// Define the structure of the JdTableModel
export class ReportsPageTableModel {
  _id: string;
  isAdhoc: boolean;
  isScheduled: boolean;
  siteId: string;
  config: any;
  reportType: string;
  pdfUrl: string;
  excelUrl: string;
  created: string;
  __v: number;
  actions: string;
}

const defineReportsTablecolumns = (
  onEditClick?: (info: CellContext<ReportsPageTableModel, any>) => void,
  onRecipientsClick?: (info: CellContext<ReportsPageTableModel, any>) => void,
  onPdfClick?: (info: CellContext<ReportsPageTableModel, any>) => void,
  onExcelClick?: (info: CellContext<ReportsPageTableModel, any>) => void,
  sites?: any[]
) => {
  // create a column helper for the JdTableModel
  const columnHelper = createColumnHelper<ReportsPageTableModel>();

  // Define the columns for the JdTable
  const defineReportsTablecolumns = [
    columnHelper.accessor("created", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.created")}</span>,
      cell: (info) => (
        <span className="">{formatDate(info.getValue(), "lastcomms")}</span>
      ),
      meta: { rowClick: false },
    }),

    columnHelper.accessor("reportType", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.report.type")}</span>,
      cell: (info) => <span className="">{info.getValue()}</span>,
      meta: { rowClick: false },
    }),

    columnHelper.accessor("isAdhoc", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.origin")}</span>,
      cell: (info) => {
        const row = info.row.original;
        return (
          <Badge
            sizes="sm"
            label={row.isAdhoc ? "Adhoc" : "Scheduled"}
            color={row.isAdhoc ? "amber" : "teal"}
          />
        );
      },
      meta: { rowClick: false },
    }),

    columnHelper.accessor("isScheduled", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.expires")}</span>,
      cell: (info) => {
        const row = info.row.original;
        const { expired, daysLeft } = expiredDate(row.created, 7);
        const label = expired ? "Expired" : `${daysLeft} days`;
        return (
          <Badge sizes="sm" label={label} color={expired ? "red" : "green"} />
        );
      },
      meta: { rowClick: false },
    }),

    // Actions column
    columnHelper.accessor("config", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.files")}</span>,
      cell: (info) => {
        const row = info.row.original;
        const { expired } = expiredDate(row.created, 7);
        return (
          <div className="flex flex-row">
            {row.pdfUrl && (
              <CircleButton
                icon={MdPictureAsPdf}
                size="sm"
                variant="ghost"
                disabled={expired}
                onClick={() => {
                  onPdfClick?.(info);
                }}
                tooltip={t("button.download.pdf")}
                tooltipPlacement="bottom"
              />
            )}
            {row.excelUrl && (
              <CircleButton
                icon={MdFilePresent}
                size="sm"
                variant="ghost"
                disabled={expired}
                onClick={() => {
                  onExcelClick?.(info);
                }}
                tooltip={t("button.download.excel")}
                tooltipPlacement="bottom"
              />
            )}
          </div>
        );
      },
    }),
  ];

  return defineReportsTablecolumns;
};

export default defineReportsTablecolumns;
